import { useEffect, useRef, useState } from 'react';

import { serverNow } from '@/helpers/serverTime';
import { announce } from '@/lib/announcer';

const TICK_MS = 1_000;

const DEFAULT_MILESTONES = [5 * 60_000, 2 * 60_000, 60_000, 30_000] as const;

export interface UseCountdownOptions {
  expiresAt: string | null;
  warnMs: number;
  onExpired: () => void;
  /** Already localized. Fired once as remaining crosses each threshold, descending. */
  milestoneMessages?: Readonly<Record<number, string>> | undefined;
  /** Already localized. The one assertive announcement in the app. */
  urgentMessage?: string | undefined;
}

function remainingFrom(expiresAt: string): number {
  const expires = Date.parse(expiresAt);
  if (!Number.isFinite(expires)) return 0;
  return expires - serverNow();
}

export function useCountdown({
  expiresAt,
  warnMs,
  onExpired,
  milestoneMessages,
  urgentMessage,
}: UseCountdownOptions): {
  remainingMs: number;
  isUrgent: boolean;
  hasExpired: boolean;
} {
  const [remainingMs, setRemainingMs] = useState(() =>
    expiresAt === null ? 0 : remainingFrom(expiresAt),
  );
  const expiredRef = useRef(false);
  const announcedMilestones = useRef(new Set<number>());
  const announcedUrgent = useRef(false);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    expiredRef.current = false;
    announcedMilestones.current.clear();
    announcedUrgent.current = false;
    if (expiresAt === null) {
      setRemainingMs(0);
      return;
    }

    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      const next = remainingFrom(expiresAt);
      setRemainingMs(next);

      if (milestoneMessages !== undefined) {
        for (const atMs of DEFAULT_MILESTONES) {
          const message = milestoneMessages[atMs];
          if (message === undefined) continue;
          if (next <= atMs && !announcedMilestones.current.has(atMs)) {
            announcedMilestones.current.add(atMs);
            announce(message, 'polite');
          }
        }
      }

      if (urgentMessage !== undefined && next <= warnMs && next > 0 && !announcedUrgent.current) {
        announcedUrgent.current = true;
        announce(urgentMessage, 'assertive');
      }

      if (next <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpiredRef.current();
      }
    };

    tick();
    const id = window.setInterval(tick, TICK_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [expiresAt, milestoneMessages, urgentMessage, warnMs]);

  const hasExpired = expiresAt !== null && remainingMs <= 0;
  return {
    remainingMs: Math.max(0, remainingMs),
    isUrgent: remainingMs > 0 && remainingMs <= warnMs,
    hasExpired,
  };
}
